import { z } from 'zod';

/**
 * Catálogo de permissões — seção 8 do PROMPT-MESTRE.
 * Formato `recurso:ação`, sempre avaliado dentro de um escopo.
 */
export const RESOURCES = [
  'people',
  'ministries',
  'functions',
  'events',
  'positions',
  'assignments',
  'swaps',
  'approvals',
  'checkins',
  'service_plans',
  'songs',
  'files',
  'notifications',
  'whatsapp',
  'reports',
  'settings',
  'billing',
  'roles',
  'audit',
  'children',
  'child_health',
  'child_checkins',
  'child_incidents',
  'guardians',
  'kids_clearances',
  'tenants',
] as const;

export const ACTIONS = [
  'read',
  'create',
  'update',
  'delete',
  'publish',
  'approve',
  'export',
  'manage',
  'override',
  'impersonate',
] as const;

export const SCOPE_KINDS = [
  'platform',
  'tenant',
  'congregation',
  'ministry',
  'classroom',
  'self',
  'guardian',
] as const;

export type Resource = (typeof RESOURCES)[number];
export type Action = (typeof ACTIONS)[number];
export type ScopeKind = (typeof SCOPE_KINDS)[number];

/** Ex.: `assignments:publish` */
export type PermissionKey = `${Resource}:${Action}`;

export const permissionKeySchema = z
  .string()
  .refine(
    (value): value is PermissionKey => {
      const [resource, action] = value.split(':');
      return (
        RESOURCES.includes(resource as Resource) && ACTIONS.includes(action as Action)
      );
    },
    { message: 'Permissão inválida' },
  );

export const scopeSchema = z.object({
  kind: z.enum(SCOPE_KINDS),
  /** Identificador do escopo quando ele é delimitado (congregação, ministério, sala). */
  id: z.string().uuid().nullable().default(null),
});
export type Scope = z.infer<typeof scopeSchema>;

export const grantSchema = z.object({
  permission: permissionKeySchema,
  scope: scopeSchema,
});
export type Grant = z.infer<typeof grantSchema>;

/** Escopos que contêm outros escopos, do mais amplo para o mais estreito. */
export const SCOPE_HIERARCHY: Record<ScopeKind, readonly ScopeKind[]> = {
  platform: ['platform', 'tenant', 'congregation', 'ministry', 'classroom', 'self', 'guardian'],
  tenant: ['tenant', 'congregation', 'ministry', 'classroom'],
  congregation: ['congregation', 'ministry', 'classroom'],
  ministry: ['ministry'],
  classroom: ['classroom'],
  self: ['self'],
  guardian: ['guardian'],
};

export function scopeContains(granted: Scope, required: Scope): boolean {
  if (!SCOPE_HIERARCHY[granted.kind].includes(required.kind)) return false;
  // Escopo amplo (platform/tenant) não precisa casar identificador.
  if (granted.kind === 'platform' || granted.kind === 'tenant') return true;
  /*
   * Concessão sem identificador é concessão não delimitada: ninguém escolheu o
   * departamento — nem a congregação, nem a sala — em que o perfil vale. Sem
   * isto, um perfil de escopo `ministry` não vale em ministério nenhum, que é
   * como o Líder de departamento acabou sem alcançar tela alguma. Quando o
   * identificador existe, ele continua sendo a trava.
   *
   * congregation contém ministry/classroom apenas via checagem explícita no
   * servidor, que resolve a congregação do recurso antes de chamar esta função.
   */
  return granted.id === null || granted.id === required.id;
}

/**
 * `manage` implica todas as demais ações sobre o mesmo recurso.
 * Nenhum outro atalho existe: `child_health:read` jamais é implicado por perfil genérico.
 */
export function grantSatisfies(granted: Grant, required: Grant): boolean {
  const [grantedResource, grantedAction] = granted.permission.split(':') as [Resource, Action];
  const [requiredResource, requiredAction] = required.permission.split(':') as [Resource, Action];
  if (grantedResource !== requiredResource) return false;
  if (grantedAction !== requiredAction && grantedAction !== 'manage') return false;
  return scopeContains(granted.scope, required.scope);
}

export function can(grants: readonly Grant[], required: Grant): boolean {
  return grants.some((granted) => grantSatisfies(granted, required));
}
